import {
  ResponseGetMerchflowStoreProp_storePropagationId_,
  RouteGetMerchflowStoreProp_storePropagationId_,
} from "@CommonApi/merchflow/store-prop/_storePropagationId_";
import {
  ResponseGetMerchflows_MerchflowId_Stores_StoreCode_,
  RouteGetMerchflows_MerchflowId_Stores_StoreCode_,
} from "@CommonApi/merchflows/_merchflowId_/stores/_storeCode_";
import {
  PayloadDeleteMerchflows_MerchflowId_StoresPogs,
  ResponseDeleteMerchflows_MerchflowId_StoresPogs,
  RouteDeleteMerchflows_MerchflowId_StoresPogs,
} from "@CommonApi/merchflows/_merchflowId_/stores/pogs/delete";
import { RequiredPaginationQueryParams } from "@CommonTypes/backend/pagination";
import { useLoaders } from "src/components/InlineLoader";
import { useToasts } from "src/components/Toasts";
import { API, deleteRequest, getRequest } from "src/utils/axios";

export const getMerchflows_MerchflowId_StoresReq = (
  merchflowId: number,
  queryParams: RequiredPaginationQueryParams,
) => {
  return getRequest<ResponseGetMerchflowStoreProp_storePropagationId_>(
    `${API}${RouteGetMerchflowStoreProp_storePropagationId_}`,
    {
      params: {
        merchflowId,
      },
      queryParams,
    },
  );
};

export const deleteMerchflows_MerchflowId_StoresPogs_Req = (
  merchflowId: number,
  body: PayloadDeleteMerchflows_MerchflowId_StoresPogs,
) =>
  deleteRequest<ResponseDeleteMerchflows_MerchflowId_StoresPogs>(
    `${API}${RouteDeleteMerchflows_MerchflowId_StoresPogs}`,
    {
      params: { merchflowId },
      payload: body,
    },
  );

export const getMerchflows_MerchflowId_Stores_StoreCode_Req = (
  merchflowId: number,
  storeCode: string,
  queryParams: RequiredPaginationQueryParams,
) => {
  return getRequest<ResponseGetMerchflows_MerchflowId_Stores_StoreCode_>(
    `${API}${RouteGetMerchflows_MerchflowId_Stores_StoreCode_}`,
    {
      params: {
        merchflowId,
        storeCode,
      },
      queryParams,
    },
  );
};

export const useMerchflowStoresApi = () => {
  const { toast } = useToasts();
  const { addLoader, removeLoader } = useLoaders();

  const getMerchflowStoresApi = async (
    setData: (data: ResponseGetMerchflowStoreProp_storePropagationId_ | null) => void,
    merchflowId: number,
    queryParams: RequiredPaginationQueryParams,
  ) => {
    addLoader("merchflow/:merchflowId/stores");

    try {
      const { data } = await getMerchflows_MerchflowId_StoresReq(merchflowId, queryParams);

      setData(data);
    } catch (error) {
      toast({
        title: "Failed to load stores",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
      setData(null);
    }

    removeLoader("merchflow/:merchflowId/stores");
  };

  const getMerchflowStorePogsApi = async ({
    merchflowId,
    storeCode,
    pagination,
    setData,
  }: {
    merchflowId: number;
    storeCode: string;
    pagination: RequiredPaginationQueryParams;
    setData: (data: ResponseGetMerchflows_MerchflowId_Stores_StoreCode_ | null) => void;
  }) => {
    addLoader(RouteGetMerchflows_MerchflowId_Stores_StoreCode_);

    try {
      const { data } = await getMerchflows_MerchflowId_Stores_StoreCode_Req(
        merchflowId,
        storeCode,
        pagination,
      );
      setData(data);
    } catch (error) {
      toast({
        title: "Failed to load store pogs",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
      setData(null);
    }

    removeLoader(RouteGetMerchflows_MerchflowId_Stores_StoreCode_);
  };

  const deleteMerchflowStorePogsApi = async (
    merchflowId: number,
    storePogIds: number[],
  ): Promise<ResponseDeleteMerchflows_MerchflowId_StoresPogs | null> => {
    addLoader(RouteDeleteMerchflows_MerchflowId_StoresPogs);
    let result = null;

    try {
      const { data } = await deleteMerchflows_MerchflowId_StoresPogs_Req(merchflowId, {
        store_pog_ids: storePogIds,
      });
      result = data;
    } catch (error) {
      toast({
        title: "Failed to delete store planograms",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
    }

    removeLoader(RouteDeleteMerchflows_MerchflowId_StoresPogs);
    return result;
  };

  return {
    getMerchflowStoresApi,
    getMerchflowStorePogsApi,
    deleteMerchflowStorePogsApi,
  };
};
